import { useCallback, useState, useEffect } from 'react';

import { apiService } from '../../services';
import type {
  PaymentInvoiceDetailPrevDTO,
  PaymentInvoiceHistoryPrevDTO,
  PaymentListRequest,
  SubCompanyResponseDTO,
} from '../../types/previdencia/previdenciaType';

export interface UseDigitalInvoicingReturn {
  // Payments data
  payments: PaymentInvoiceHistoryPrevDTO[];
  isLoadingPayments: boolean;
  currentPage: number;
  totalPages: number;
  totalElements: number;
  pageSize: number;

  // Subcompanies data
  subCompanies: SubCompanyResponseDTO[];
  isLoadingSubCompanies: boolean;

  // Payment detail
  paymentDetail: PaymentInvoiceDetailPrevDTO | null;
  isLoadingDetail: boolean;

  // Search state
  hasSearchResults: boolean;
  hasSearched: boolean;
  currentSearchParams: PaymentListRequest | null;

  // Actions
  searchPayments: (params: PaymentListRequest) => Promise<void>;
  changePage: (page: number) => Promise<void>;
  loadSubCompanies: (companyId: string) => Promise<void>;
  getPaymentDetail: (invoiceId: number) => Promise<void>;
  clearPaymentDetail: () => void;
  clearSearch: () => void;
  clearError: () => void;
}

export const useDigitalInvoicing = (companyId: string): UseDigitalInvoicingReturn => {
  // Payments data states
  const [payments, setPayments] = useState<PaymentInvoiceHistoryPrevDTO[]>([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize] = useState(10); // Fixed page size

  // Subcompanies states
  const [subCompanies, setSubCompanies] = useState<SubCompanyResponseDTO[]>([]);
  const [isLoadingSubCompanies, setIsLoadingSubCompanies] = useState(false);

  // Payment detail states
  const [paymentDetail, setPaymentDetail] = useState<PaymentInvoiceDetailPrevDTO | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  // Search state
  const [hasSearchResults, setHasSearchResults] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [currentSearchParams, setCurrentSearchParams] = useState<PaymentListRequest | null>(null);

  // Load subcompanies when companyId changes
  const loadSubCompanies = useCallback(async (companyIdToLoad: string): Promise<void> => {
    if (!companyIdToLoad) {
      setSubCompanies([]);
      return;
    }

    setIsLoadingSubCompanies(true);
    try {
      const subCompaniesList = await apiService.subCompany.getSubCompanies(companyIdToLoad);
      setSubCompanies(subCompaniesList);
    } catch (err) {
      setSubCompanies([]);
      throw err;
    } finally {
      setIsLoadingSubCompanies(false);
    }
  }, []);

  // Auto-load subcompanies when companyId changes
  useEffect(() => {
    if (companyId) {
      loadSubCompanies(companyId);
    } else {
      setSubCompanies([]);
    }
  }, [companyId, loadSubCompanies]);

  // Search payments
  const searchPayments = useCallback(async (params: PaymentListRequest): Promise<void> => {
    setIsLoadingPayments(true);

    // Reset states before performing new search
    setHasSearchResults(false);
    setHasSearched(false);
    setPayments([]);
    setCurrentPage(1);
    setTotalPages(0);
    setTotalElements(0);

    try {
      const searchParams: PaymentListRequest = {
        ...params,
        page: 0, // Backend uses 0-based pagination
        size: pageSize,
      };

      setCurrentSearchParams(searchParams);

      const response = await apiService.digitalInvoicing.getPayments(searchParams);
      setPayments(response.content || []);
      setTotalPages(response.totalPages || 0);
      setTotalElements(response.totalElements || 0);
      setCurrentPage(1); // UI uses 1-based pagination

      if (response.content && response.content.length > 0) {
        setHasSearchResults(true);
        setHasSearched(true);
      } else {
        setHasSearchResults(false);
        setHasSearched(true);
      }
    } catch (err) {
      setPayments([]);
      setHasSearchResults(false);
      setHasSearched(true);
      throw err;
    } finally {
      setIsLoadingPayments(false);
    }
  }, [pageSize]);

  // Change page
  const changePage = useCallback(
    async (page: number): Promise<void> => {
      if (!currentSearchParams) {
        throw new Error('Por favor, realize uma busca primeiro');
      }

      // Prevent unnecessary requests if clicking on the same page
      if (page === currentPage) {
        return;
      }

      setIsLoadingPayments(true);

      try {
        const searchParams: PaymentListRequest = {
          ...currentSearchParams,
          page: page - 1, // Convert UI page (1-based) to backend page (0-based)
          size: pageSize,
        };

        setCurrentSearchParams(searchParams);

        const response = await apiService.digitalInvoicing.getPayments(searchParams);
        setPayments(response.content || []);
        setTotalPages(response.totalPages || 0);
        setTotalElements(response.totalElements || 0);
        setCurrentPage(page);
      } finally {
        setIsLoadingPayments(false);
      }
    },
    [currentSearchParams, currentPage, pageSize]
  );

  // Clear search
  const clearSearch = useCallback(() => {
    setPayments([]);
    setHasSearchResults(false);
    setHasSearched(false);
    setCurrentPage(1);
    setTotalPages(0);
    setTotalElements(0);
    setCurrentSearchParams(null);
  }, []);

  // Get payment detail
  const getPaymentDetail = useCallback(async (invoiceId: number): Promise<void> => {
    setIsLoadingDetail(true);

    try {
      const detail = await apiService.digitalInvoicing.getPaymentDetail(invoiceId);
      setPaymentDetail(detail);
    } catch (err) {
      setPaymentDetail(null);
      throw err;
    } finally {
      setIsLoadingDetail(false);
    }
  }, []);

  // Clear payment detail
  const clearPaymentDetail = useCallback(() => {
    setPaymentDetail(null);
  }, []);

  // Clear error (for consistency with other hooks)
  const clearError = useCallback(() => {
    // Error handling is done via try/catch, but keeping for consistency
  }, []);

  return {
    // Payments data
    payments,
    isLoadingPayments,
    currentPage,
    totalPages,
    totalElements,
    pageSize,

    // Subcompanies data
    subCompanies,
    isLoadingSubCompanies,

    // Payment detail
    paymentDetail,
    isLoadingDetail,

    // Search state
    hasSearchResults,
    hasSearched,
    currentSearchParams,

    // Actions
    searchPayments,
    changePage,
    loadSubCompanies,
    getPaymentDetail,
    clearPaymentDetail,
    clearSearch,
    clearError,
  };
};

